// 数据持久化服务
export const STORAGE_KEYS = {
  AUTH_LIST: 'auth_list',
  AUTH_RECYCLE: 'auth_recycle',
  USER_INFO: 'user_info',
  TOKEN: 'token',
  SITE_SETTINGS: 'site_settings',
  SECURITY_SETTINGS: 'security_settings',
  STATISTICS_OVERVIEW: 'statistics_overview',
  STATISTICS_TREND: 'statistics_trend',
  STATISTICS_RANKING: 'statistics_ranking',
  STATISTICS_STATUS: 'system_status',
  SMS_SERVICE: {
    PROVIDERS: 'sms_providers',
    PACKAGES: 'sms_packages',
    APPLICATIONS: 'sms_applications',
    ORDERS: 'sms_recharge_list',
    BALANCES: 'sms_balances'
  },
  INSURANCE_SERVICE: {
    APPLICATIONS: 'insurance_applications',
    RECHARGES: 'insurance_recharge_list',
    BALANCES: 'insurance_balances',
    SETTINGS: 'insurance_settings'
  }
}

// 默认数据
const DEFAULT_DATA = {
  [STORAGE_KEYS.AUTH_LIST]: [
    {
      id: 1,
      auth_code: 'AUTH001',
      auth_number: 'AN20231201001',
      client_name: '测试客户1',
      phone: '13800138001',
      domain: 'test1.example.com',
      ip_address: '192.168.1.1',
      platform_limit: 5,
      expire_date: '2024-12-31',
      status: '正常',
      created_at: '2023-12-01T08:00:00.000Z',
      updated_at: '2023-12-01T08:00:00.000Z'
    },
    {
      id: 2,
      auth_code: 'AUTH002',
      auth_number: 'AN20231201002',
      client_name: '测试客户2',
      phone: '13800138002',
      domain: 'test2.example.com',
      ip_address: '192.168.1.2',
      platform_limit: 3,
      expire_date: '2024-06-30',
      status: '正常',
      created_at: '2023-12-01T09:00:00.000Z',
      updated_at: '2023-12-01T09:00:00.000Z'
    }
  ],
  [STORAGE_KEYS.SMS_SERVICE.PROVIDERS]: [],
  [STORAGE_KEYS.SMS_SERVICE.PACKAGES]: [],
  [STORAGE_KEYS.SMS_SERVICE.APPLICATIONS]: [],
  [STORAGE_KEYS.SMS_SERVICE.ORDERS]: [],
  [STORAGE_KEYS.SMS_SERVICE.BALANCES]: [],
  [STORAGE_KEYS.SITE_SETTINGS]: {
    siteName: '授权管理系统',
    siteDesc: '专业的授权管理解决方案',
    loginTitle: '授权管理系统',
    loginSubtitle: '欢迎使用授权管理系统',
    copyright: '© 2024 授权管理系统',
    icp: '粤ICP备xxxxxxxx号',
    version: 'v1.0.0',
    techSupport: '技术支持：SAAS科技',
    servicePhone: '400-XXX-XXXX'
  },
  [STORAGE_KEYS.SECURITY_SETTINGS]: {
    passwordStrength: 'medium',
    minPasswordLength: 8,
    loginLockEnabled: true,
    maxLoginAttempts: 5,
    lockDuration: 30,
    sessionTimeout: 120,
    ipWhitelist: ''
  },
  // 统计概览数据
  [STORAGE_KEYS.STATISTICS_OVERVIEW]: {
    totalAuth: 125,
    totalAuthTrend: 5.8,
    validAuth: 98,
    validAuthRate: 78.4,
    expiringAuth: 15,
    expiringAuthRate: 12,
    expiredAuth: 12,
    expiredAuthRate: 9.6
  },
  // 趋势数据
  [STORAGE_KEYS.STATISTICS_TREND]: {
    dates: ['12-01', '12-02', '12-03', '12-04', '12-05', '12-06', '12-07'],
    newAuth: [5, 8, 3, 6, 10, 4, 7],
    expireAuth: [2, 3, 1, 4, 2, 5, 3]
  },
  // 排行数据
  [STORAGE_KEYS.STATISTICS_RANKING]: {
    plugin: [
      { name: '顾客端APP', count: 68 },
      { name: '商户APP', count: 52 },
      { name: '配送员APP', count: 45 },
      { name: '平台管理端APP', count: 38 },
      { name: '平台装修', count: 32 },
      { name: '外卖小程序', count: 28 }
    ],
    service: {
      sms: {
        count: 1256,
        usage: 62.8,
        color: '#409EFF'
      },
      insurance: {
        count: 386,
        usage: 38.6,
        color: '#67C23A'
      }
    }
  },
  // 系统状态
  [STORAGE_KEYS.STATISTICS_STATUS]: {
    uptime: '15天8小时26分',
    cpu: 45.2,
    memory: 68.5,
    disk: 72.3
  }
}

class StorageService {
  constructor() {
    // 初始化存储
    this.initStorage()
  }

  // 初始化存储
  initStorage() {
    // 初始化默认数据，但排除AUTH_LIST（从后端获取）
    Object.entries(DEFAULT_DATA).forEach(([key, value]) => {
      // 跳过AUTH_LIST，让它从后端API获取
      if (key === STORAGE_KEYS.AUTH_LIST) {
        return
      }
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, JSON.stringify(value))
      }
    })
  }

  // 通用 CRUD 方法
  get(key) {
    try {
      return JSON.parse(localStorage.getItem(key))
    } catch (error) {
      console.error(`Error getting data for key ${key}:`, error)
      return null
    }
  }

  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value))
      return true
    } catch (error) {
      console.error(`Error setting data for key ${key}:`, error)
      return false
    }
  }

  remove(key) {
    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error(`Error removing data for key ${key}:`, error)
      return false
    }
  }

  clear() {
    try {
      localStorage.clear()
      this.initStorage()
      return true
    } catch (error) {
      console.error('Error clearing storage:', error)
      return false
    }
  }

  // 列表数据操作方法
  getList(key) {
    return this.get(key) || []
  }

  addItem(key, item) {
    const list = this.getList(key)
    list.push({
      ...item,
      id: Date.now(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    return this.set(key, list)
  }

  updateItem(key, id, item) {
    const list = this.getList(key)
    const index = list.findIndex(i => i.id === id)
    if (index > -1) {
      list[index] = {
        ...list[index],
        ...item,
        updated_at: new Date().toISOString()
      }
      return this.set(key, list)
    }
    return false
  }

  deleteItem(key, id) {
    const list = this.getList(key)
    const newList = list.filter(i => i.id !== id)
    return this.set(key, newList)
  }

  // 分页方法
  getPage(key, { page = 1, pageSize = 10, filters = {} } = {}) {
    const list = this.getList(key)
    
    // 应用过滤
    let filteredList = list
    Object.entries(filters).forEach(([field, value]) => {
      if (value) {
        filteredList = filteredList.filter(item => 
          String(item[field]).toLowerCase().includes(String(value).toLowerCase())
        )
      }
    })
    
    // 计算分页
    const total = filteredList.length
    const start = (page - 1) * pageSize
    const end = start + pageSize
    const data = filteredList.slice(start, end)
    
    return {
      list: data,
      total,
      page,
      pageSize
    }
  }

  // 特定业务方法
  // 权限列表
  getAuthList() {
    return this.get(STORAGE_KEYS.AUTH_LIST) || []
  }

  setAuthList(list) {
    return this.set(STORAGE_KEYS.AUTH_LIST, list)
  }

  getAuthRecycle() {
    return this.getList(STORAGE_KEYS.AUTH_RECYCLE)
  }

  setAuthRecycle(list) {
    return this.set(STORAGE_KEYS.AUTH_RECYCLE, list)
  }

  // 回收站操作
  moveToRecycleBin(auth) {
    const authList = this.getAuthList()
    const recycleBin = this.getAuthRecycle()
    
    // 从授权列表中移除
    const newAuthList = authList.filter(item => item.id !== auth.id)
    this.setAuthList(newAuthList)
    
    // 添加到回收站
    recycleBin.unshift({
      ...auth,
      deleteTime: new Date().toISOString()
    })
    this.setAuthRecycle(recycleBin)
    
    return true
  }

  restoreFromRecycleBin(id) {
    const authList = this.getAuthList()
    const recycleBin = this.getAuthRecycle()
    
    // 找到要恢复的项目
    const itemToRestore = recycleBin.find(item => item.id === id)
    if (!itemToRestore) return false
    
    // 从回收站移除
    const newRecycleBin = recycleBin.filter(item => item.id !== id)
    this.setAuthRecycle(newRecycleBin)
    
    // 恢复到授权列表
    const restoredItem = { ...itemToRestore }
    delete restoredItem.deleteTime // 删除删除时间标记
    authList.unshift(restoredItem)
    this.setAuthList(authList)
    
    return true
  }

  permanentlyDelete(id) {
    const recycleBin = this.getAuthRecycle()
    const newRecycleBin = recycleBin.filter(item => item.id !== id)
    return this.setAuthRecycle(newRecycleBin)
  }

  // 统计相关方法
  getStatisticsOverview() {
    return this.get(STORAGE_KEYS.STATISTICS_OVERVIEW)
  }

  getStatisticsTrend(days = 7) {
    const data = this.get(STORAGE_KEYS.STATISTICS_TREND)
    if (days === 7) {
      return data
    }
    // 生成30天数据
    const dates = []
    const newAuth = []
    const expireAuth = []
    for (let i = 0; i < 30; i++) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      dates.unshift(`${date.getMonth() + 1}-${date.getDate()}`)
      newAuth.unshift(Math.floor(Math.random() * 15))
      expireAuth.unshift(Math.floor(Math.random() * 8))
    }
    return { dates, newAuth, expireAuth }
  }

  getStatisticsRanking(type) {
    const data = this.get(STORAGE_KEYS.STATISTICS_RANKING)
    return data[type]
  }

  getSystemStatus() {
    return this.get(STORAGE_KEYS.STATISTICS_STATUS)
  }

  // 数据一致性检查
  validateDataConsistency() {
    try {
      const authList = this.getAuthList()
      const recycleBin = this.getAuthRecycle()
      
      // 检查ID是否唯一
      const allIds = [...authList, ...recycleBin].map(item => item.id)
      const uniqueIds = new Set(allIds)
      if (allIds.length !== uniqueIds.size) {
        console.error('发现重复的ID')
        return false
      }
      
      // 检查必要字段
      const requiredFields = ['id', 'auth_code', 'auth_number', 'client_name', 'phone', 'domain', 'ip_address', 'expire_date']
      const validateItem = (item) => {
        return requiredFields.every(field => item.hasOwnProperty(field))
      }
      
      if (!authList.every(validateItem) || !recycleBin.every(validateItem)) {
        console.error('发现缺少必要字段的记录')
        return false
      }
      
      return true
    } catch (error) {
      console.error('数据一致性检查失败:', error)
      return false
    }
  }

  // 导出数据
  exportData() {
    try {
      const data = {
        authList: this.getAuthList(),
        recycleBin: this.getAuthRecycle(),
        operationLogs: JSON.parse(localStorage.getItem('auth_operation_logs') || '[]'),
        exportTime: new Date().toISOString()
      }
      return JSON.stringify(data, null, 2)
    } catch (error) {
      console.error('导出数据失败:', error)
      throw error
    }
  }

  // 导入数据
  importData(jsonString) {
    try {
      const data = JSON.parse(jsonString)
      if (!data.authList || !data.recycleBin) {
        throw new Error('无效的数据格式')
      }

      // 验证导入数据的完整性
      const tempStorage = new StorageService()
      tempStorage.setAuthList(data.authList)
      tempStorage.setAuthRecycle(data.recycleBin)
      if (!tempStorage.validateDataConsistency()) {
        throw new Error('导入数据验证失败')
      }

      // 导入数据
      this.setAuthList(data.authList)
      this.setAuthRecycle(data.recycleBin)
      if (data.operationLogs) {
        localStorage.setItem('auth_operation_logs', JSON.stringify(data.operationLogs))
      }

      return true
    } catch (error) {
      console.error('导入数据失败:', error)
      throw error
    }
  }

  // 自动备份
  autoBackup() {
    try {
      const backupData = this.exportData()
      const backupTime = new Date().toISOString().replace(/[:.]/g, '-')
      localStorage.setItem(`auth_backup_${backupTime}`, backupData)
      
      // 保留最近10个备份
      const backupKeys = Object.keys(localStorage)
        .filter(key => key.startsWith('auth_backup_'))
        .sort()
        .reverse()
      
      if (backupKeys.length > 10) {
        backupKeys.slice(10).forEach(key => localStorage.removeItem(key))
      }
      
      return true
    } catch (error) {
      console.error('自动备份失败:', error)
      return false
    }
  }

  // 恢复到指定备份
  restoreFromBackup(backupKey) {
    try {
      const backupData = localStorage.getItem(backupKey)
      if (!backupData) {
        throw new Error('备份不存在')
      }
      
      return this.importData(backupData)
    } catch (error) {
      console.error('从备份恢复失败:', error)
      throw error
    }
  }

  // 获取所有备份
  getBackupList() {
    return Object.keys(localStorage)
      .filter(key => key.startsWith('auth_backup_'))
      .map(key => ({
        key,
        time: key.replace('auth_backup_', '').replace(/-/g, ':'),
        size: localStorage.getItem(key).length
      }))
      .sort((a, b) => b.time.localeCompare(a.time))
  }
}

export const storage = new StorageService()
export default storage